import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { DataTable, DataTablePageParams, DataTableRowClickEventParams } from "primereact/datatable"
import { Column } from "primereact/column"
import { Button } from "primereact/button"
import { createUrlParams } from "../../../api/client"
import { storage } from "../../../common/storage"
import { InputText } from "primereact/inputtext"
import { useDebounce } from "../../../common/useDebounce"
import { GetOrderTemplatesRequestQuery, OrderTemplate, OrderTemplateDetail } from "../../../api/types/order-template"
import { useOrderTemplates } from "../../../api/queries/order-templates/useOrderTemplates"
import { RadioButton, RadioButtonChangeParams } from "primereact/radiobutton"
import { useOrderTemplateIsDefault } from "../../../api/queries/order-templates/useOrderTemplateIsDefault"

export default function OrderTemplateList() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orderTemplateIsDefault = useOrderTemplateIsDefault()

  const offsetParam: string = searchParams.get("offset") || ""
  const limitParam: string = searchParams.get("limit") || ""
  const [offset, setOffset] = useState<number>(parseInt(offsetParam) || 0)
  const [limit, setLimit] = useState<number>(parseInt(limitParam) || storage.get("limit") || 20)

  const onPage = ({ first, rows }: DataTablePageParams): void => {
    setOffset(first)
    setLimit(rows)
    storage.set("limit", rows)
  }

  const onNewOrderTemplateClick = async () => {
    navigate("/order-templates/new")
  }

  const onRowClick = (row: DataTableRowClickEventParams) => {
    navigate(`/order-templates/${row.data.uuid}`)
  }

  const rowClassName = (orderTemplate: OrderTemplate) => ({
    "cursor-pointer": true,
  })

  const DELAY = 500

  // name
  const [name, setName] = useState<string>(searchParams.get("fName") || "")
  const onNameChange = (event: ChangeEvent<HTMLInputElement>): void => setName(event.target.value)
  const tName = (row: OrderTemplate): string => row.name
  const fName = useDebounce(name, DELAY)
  const fNameEl = (): ReactNode => <InputText value={name} onChange={onNameChange}
    placeholder={"Název"} />

  const onIsDefaultChange = async (event: RadioButtonChangeParams) => {
    await orderTemplateIsDefault.mutateAsync({
      uuid: event.value,
      isDefault: true
    })
  }

  const tIsDefault = (row: OrderTemplateDetail) => (
    <RadioButton name="isDefault" value={row.uuid} onChange={onIsDefaultChange} checked={row.isDefault} />
  )

  let payload: GetOrderTemplatesRequestQuery = useMemo((): GetOrderTemplatesRequestQuery => ({
    limit,
    offset,
    fName,
  }), [limit, offset, fName])

  const { data: orderTemplates, isLoading } = useOrderTemplates(payload)

  useEffect(() => {
    navigate({
      search: createUrlParams(payload),
    }, {
      replace: true,
    })
  }, [payload, navigate, searchParams])

  return (
    <>
      <div className={"col-12"}>
        <div className={"flex align-items-center"}>
          <Button label={"Nová šablona"} icon={"pi pi-plus"} onClick={onNewOrderTemplateClick}
            className={"p-button p-button-primary p-button-sm mr-3"} />
        </div>
      </div>

      <div className={"col-12"}>
        <DataTable
          emptyMessage={"Žádné záznamy"} className={"p-datatable-sm p-datatable-filters"}
          paginator={true} lazy={true} rows={limit} first={offset}
          totalRecords={orderTemplates?.results.totalCount}
          loading={isLoading} onPage={onPage} value={orderTemplates?.results.orderTemplateList}
          onRowClick={onRowClick} rowClassName={rowClassName} rowHover={true} stripedRows={true}
          paginatorTemplate={"FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"}
          rowsPerPageOptions={[20, 50, 100]}
          filterDisplay={"row"}>

          <Column header={"Název"} body={tName}
            filter={true} filterElement={fNameEl} showFilterMenu={false} />

          <Column header={"Výchozí šablona"} body={tIsDefault} />
        </DataTable>
      </div>
    </>
  )
}
